import { Container } from "@carrotfertility/carotene-core"
import { useQuery } from "@tanstack/react-query"
import React, { FC } from "react"
import { useIntl } from "react-intl"
import { FullScreenLoading } from "#/components/read/shared/FullScreenLoading"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { getContentfulClient } from "#/lib/contentful/contentfulClient"
import { getContentfulLocale } from "#/lib/contentful/utils/contentfulLocaleUtils"
import { ContentfulContentRenderer } from "#/services/contentful"
import { Title } from "#/services/page-title/Title"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { createErrorBoundary } from "#/utils/createErrorBoundary"
import type { EntryWithoutUnresolvableLinks } from "#/utils/ContentTypes"
import type { TypePageSkeleton } from "#/types/contentful"

// DBG "Pharmacy" page. Consumer Carrot Rx reuses the same Contentful entry
// so pharmacy copy stays in one place.
const PHARMACY_PAGE_ENTRY_ID = "7lYoDsDsGORxTtEBQmsoiZ"
const GET_PHARMACY_PAGE_QUERY_KEY = "GET_CONSUMER_CARROT_RX_PAGE"

type PharmacyPage = EntryWithoutUnresolvableLinks<TypePageSkeleton>

const getPharmacyPage = async (locale: string): Promise<PharmacyPage> => {
  const client = getContentfulClient()
  const entry = await client.withoutUnresolvableLinks.getEntry<TypePageSkeleton>(PHARMACY_PAGE_ENTRY_ID, {
    include: 10,
    locale: getContentfulLocale(locale)
  })
  return entry as PharmacyPage
}

const ConsumerCarrotRxErrorBoundary = createErrorBoundary(ErrorPage)

const ConsumerCarrotRxContent: FC = () => {
  const { locale } = useIntl()
  const { data, isLoading, isError } = useQuery([GET_PHARMACY_PAGE_QUERY_KEY, locale], () => getPharmacyPage(locale), {
    refetchOnWindowFocus: false,
    retry: buildCarrotApiRetryPolicy(3)
  })

  if (isLoading) return <FullScreenLoading />
  if (isError || !data) return <ErrorPage />

  const { content } = data.fields
  return (
    <Container maxWidth={false} disableGutters component="article">
      <ContentfulContentRenderer contents={content} overrides={{ br: (): null => null }} />
    </Container>
  )
}

export function ConsumerCarrotRxPage(): JSX.Element {
  const intl = useIntl()
  const pageTitle = intl.formatMessage({ defaultMessage: "Carrot Rx" })
  return (
    <PageLayout variant="default" header={<PageHeader pageTitle={pageTitle} />}>
      <Title title={pageTitle} />
      <ConsumerCarrotRxErrorBoundary>
        <ConsumerCarrotRxContent />
      </ConsumerCarrotRxErrorBoundary>
    </PageLayout>
  )
}
